/**
 * Tiny stale-while-revalidate cache for dashboard API responses.
 *
 * - In-memory Map for hot reads (instant) + sessionStorage mirror so the
 *   cache survives across route changes within the same tab.
 * - `swr(key, fetcher)` returns the cached value immediately (or null) and
 *   triggers a background revalidation that updates the cache when fresh
 *   data lands.
 * - `prefetchAll([{key, fetcher}, …])` is the kick-off used at AppShell
 *   mount so the first paint on every dashboard surface is instant.
 * - `setActiveScope(userId)` namespaces every key so when a different
 *   user signs in on the same device the new account never reads the
 *   previous user's data. Call this from AuthContext on user resolve and
 *   on logout (pass null). All getCached/setCached/prefetchAll calls go
 *   through the active scope transparently.
 *
 * Keep this simple — no TTL. We invalidate the whole namespace on
 * login/logout to avoid leaking data across sessions.
 */
const PREFIX = "kiddo_cache_v1::";
const mem = new Map();

// Active scope (typically the current user.id). Read synchronously so
// cache hits at route mount remain instant even if the scope changed mid
// session.
let activeScope = "anon";

function scoped(key) {
  return `${activeScope}::${key}`;
}

export function setActiveScope(scope) {
  const next = scope || "anon";
  if (next === activeScope) return;
  activeScope = next;
}

function readSession(key) {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return undefined;
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function writeSession(key, value) {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* quota exceeded / disabled — ignore */
  }
}

export function getCached(key) {
  const sKey = scoped(key);
  if (mem.has(sKey)) return mem.get(sKey);
  const s = readSession(sKey);
  if (s !== undefined) {
    mem.set(sKey, s);
    return s;
  }
  return undefined;
}

export function setCached(key, value) {
  const sKey = scoped(key);
  mem.set(sKey, value);
  writeSession(sKey, value);
}

export function clearCache(prefix) {
  if (!prefix) {
    mem.clear();
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(PREFIX)) sessionStorage.removeItem(k);
      }
    } catch {
      /* ignore */
    }
    return;
  }
  for (const k of Array.from(mem.keys())) {
    if (k.startsWith(prefix)) mem.delete(k);
  }
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX + prefix)) sessionStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Stale-while-revalidate fetch. Returns the cached value (or undefined) and
 * a `fresh` promise that resolves once the background revalidation is done.
 *
 *   const { cached, fresh } = swr("key", () => api.get())
 *   if (cached) setState(cached);
 *   fresh.then(setState).catch(noop);
 */
export function swr(key, fetcher) {
  const cached = getCached(key);
  const fresh = Promise.resolve()
    .then(fetcher)
    .then((value) => {
      setCached(key, value);
      return value;
    });
  return { cached, fresh };
}

/**
 * Fire a batch of fetchers in parallel and store each result in cache.
 * Failures are swallowed (best-effort prefetch must not interrupt navigation).
 */
export function prefetchAll(jobs) {
  return Promise.allSettled(
    (jobs || []).map(async ({ key, fetcher }) => {
      try {
        const value = await fetcher();
        setCached(key, value);
        return value;
      } catch {
        return null;
      }
    }),
  );
}